import { warnOnce } from './logger';
import type { EventTracker } from '../../domain/interfaces/EventTracker';

interface GoKwikEventPayload {
  category: string;
  name: string;
  action: string;
  attributes: Record<string, unknown>;
}

interface GoKwikAnalyticsSDK {
  trackEvent: (payload: GoKwikEventPayload) => void;
}

declare global {
  interface Window {
    KwikAnalytics?: GoKwikAnalyticsSDK;
  }
}

export interface GoKwikConfig {
  enabled?: boolean;
  /**
   * When false (default), only page-view/system events are sent.
   * When true, generic analytics.trackEvent(...) can flow to GoKwik too.
   */
  trackAllEvents?: boolean;
}

/** Legacy parity: always forwarded to GoKwik even when trackAllEvents is false. */
const GOKWIK_ALWAYS_CUSTOM_EVENTS = new Set(['Added To Cart', 'Added To Wishlist']);

function isGoKwikAlwaysCustomEvent(eventName: string): boolean {
  return GOKWIK_ALWAYS_CUSTOM_EVENTS.has(eventName);
}

export class GoKwikAdapter implements EventTracker {
  private readonly config: GoKwikConfig;
  private initialized = false;

  constructor(config: GoKwikConfig = {}) {
    this.config = {
      enabled: true,
      trackAllEvents: false,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (!this.config.enabled) {
      return;
    }
    this.initialized = true;
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.config.trackAllEvents) {
      if (eventName === 'Home Page Viewed') {
        this.logGoKwikSystemEvent('other');
        return;
      }
      if (isGoKwikAlwaysCustomEvent(eventName)) {
        this.sendGoKwikCustomEvent(eventName, properties);
      }
      return;
    }
    this.sendGoKwikCustomEvent(eventName, properties);
  }

  logGoKwikEvent(
    eventName: string,
    attributes: Record<string, unknown> = {},
    eventCategory?: string
  ): void {
    if (!this.config.enabled || !this.initialized) {
      return;
    }

    if (!this.config.trackAllEvents && !isGoKwikAlwaysCustomEvent(eventName)) {
      warnOnce(
        'gokwik:custom-events-disabled',
        'GoKwik custom events are disabled. Enable VITE_GO_KWIK_ALL_EVENTS=true to send all events.'
      );
      return;
    }

    this.sendGoKwikCustomEvent(eventName, attributes, eventCategory);
  }

  private sendGoKwikCustomEvent(
    eventName: string,
    attributes: Record<string, unknown> = {},
    eventCategory?: string
  ): void {
    if (!this.config.enabled || !this.initialized) {
      return;
    }

    if (typeof window === 'undefined' || !window.KwikAnalytics?.trackEvent) {
      warnOnce('gokwik:unavailable', 'GoKwik is not available on the window object.');
      return;
    }

    try {
      window.KwikAnalytics.trackEvent({
        category: eventCategory || 'custom_event_type',
        name: eventName,
        action: 'user_action',
        attributes,
      });
    } catch (error) {
      console.error('Error tracking GoKwik event:', error);
    }
  }

  logGoKwikSystemEvent(type: string, attributes: Record<string, unknown> = {}): void {
    if (!this.config.enabled || !this.initialized) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
      warnOnce('gokwik:dispatch-unavailable', 'GoKwik is not available on the window object.');
      return;
    }

    try {
      const event = new CustomEvent('page_view_kp', {
        detail: {
          type: type || 'other',
          data: attributes,
        },
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error dispatching GoKwik system event:', error);
    }
  }
}
